import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/ui';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import type { WidgetDef } from './types';

interface SettingsPanelProps {
  dark: boolean;
  widgets: WidgetDef[];
  widgetSettings: (id: string) => { visible: boolean; mode: 'full' | 'summary' };
  setWidgetSettings: (id: string, s: Partial<{ visible: boolean; mode: 'full' | 'summary' }>) => void;
  /** Paket 2310e: Dashboard je Bereich (Startseite): Quelle, Zurücksetzen, Standard für alle. */
  areaControls?: {
    source: 'user' | 'default' | 'builtin';
    canSetDefault: boolean;
    resetToDefault: () => Promise<void>;
    saveAsDefault: () => Promise<void>;
    clearDefault: () => Promise<void>;
  };
}

export function SettingsPanel({ dark, widgets, widgetSettings, setWidgetSettings, areaControls }: SettingsPanelProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const run = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const areaBtn: React.CSSProperties = {
    background: 'none', border: `1px solid ${t.border}`, borderRadius: 7, padding: '4px 10px',
    cursor: busy ? 'default' : 'pointer', color: t.text, fontSize: 12, opacity: busy ? 0.6 : 1,
  };
  return (
    <Card dark={dark} style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 12 }}>
        {tr('dashboard.settings_panel.title')}
      </div>
      {areaControls && (
        <div data-testid="dashboard-area-scope" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 11.5, color: t.muted }}>{tr(`dashboard.settings_panel.source_${areaControls.source}`)}</span>
          {areaControls.source === 'user' && (
            <button type="button" disabled={busy} style={areaBtn} onClick={() => run(areaControls.resetToDefault)}>
              {tr('dashboard.settings_panel.reset_to_default')}
            </button>
          )}
          {areaControls.canSetDefault && (
            <>
              <button type="button" disabled={busy} style={areaBtn} onClick={() => run(areaControls.saveAsDefault)}>
                {tr('dashboard.settings_panel.save_as_default')}
              </button>
              <button type="button" disabled={busy} style={areaBtn} onClick={() => run(areaControls.clearDefault)}>
                {tr('dashboard.settings_panel.clear_default')}
              </button>
            </>
          )}
        </div>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 10,
      }}>
        {widgets.map(w => {
          const ws = widgetSettings(w.id);
          return (
            <div key={w.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', border: `1px solid ${t.borderSoft}`, borderRadius: 10,
              background: t.cardMuted,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: t.heading }}>
                  {tr(w.titleKey)}
                </span>
                {ws.visible && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(['full', 'summary'] as const).map(mode => (
                      <button
                        key={mode}
                        onClick={() => setWidgetSettings(w.id, { mode })}
                        style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                          border: `1px solid ${ws.mode === mode ? BRAND : t.border}`,
                          background: ws.mode === mode ? BRAND_LIGHT : 'transparent',
                          color: ws.mode === mode ? BRAND : t.muted,
                          cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
                        }}
                      >
                        {mode === 'full' ? tr('dashboard.widget.mode_full') : tr('dashboard.widget.mode_summary')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={ws.visible}
                  onChange={e => setWidgetSettings(w.id, { visible: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: BRAND, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 12, color: t.muted }}>
                  {ws.visible ? tr('dashboard.settings_panel.on') : tr('dashboard.settings_panel.off')}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
